/**
 * Emit hosts for the `templates` analytics group — 3 functions
 * lifted out of `analyticsScribeModel`'s effects so a call site can emit
 * without holding a Rematch `dispatch`. Every payload is the effect's own.
 */
import { track } from '@/lib/analytics/track';
import { trackLegacyEvent } from '@/lib/analytics/track-legacy-event';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import {
  type RequestMethod,
  type TemplateCreationSource,
  type TemplateQueueSource,
} from '@/models/analytics/features/templates';
import { SegmentActionName, SegmentObjectName } from '@/models/analytics/segment-names';
import { type TemplateVisibility } from '@heidi/schema/ml-scribe';

export function trackTemplateCreated({
  templateId,
  templateName,
  teamId,
  version,
  sectionCount,
  source,
  aiCommandText,
  useSections,
}: {
  templateId?: string;
  templateName?: string;
  teamId?: string;
  version?: 'v1' | 'v2';
  sectionCount?: number;
  source?: TemplateCreationSource;
  /** The prompt behind an `ask_ai` creation. */
  aiCommandText?: string;
  useSections?: boolean;
}) {
  track({
    event_name: TrackingEventName.TemplateCreated,
    props: {
      src: source ?? 'default',
      template_id: templateId,
      template_name: templateName,
      team_id: teamId,
      template_editor_version: version,
      section_count: sectionCount,
      ai_command_text: aiCommandText,
      use_sections: useSections,
    },
  });
}

export function trackTemplateCreationRecordCreated({
  requestMethods,
}: {
  requestMethods: RequestMethod[];
}) {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.TemplateCreationRecordCreated,
    props: {
      request_methods: requestMethods,
    },
  });
}

export function trackTemplatePinned({
  templateId,
  templateType,
  templatePinned,
  queueSource,
  currentQueueSize,
}: {
  templateId: string;
  templateType: TemplateVisibility;
  templatePinned: boolean;
  /** Surface that triggered the pin/queue toggle. */
  queueSource?: TemplateQueueSource;
  /** Queue size after this change. */
  currentQueueSize?: number;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.Template,
    props: {
      action: SegmentActionName.TemplatePinned,
      templateId,
      templateType,
      templatePinned,
    },
  });

  // V2 — Amplitude + Datadog only
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.TemplateUpdated,
    props: {
      template_id: templateId,
      update_action: 'pinned',
      template_type: templateType,
      is_pinned: templatePinned,
      queue_source: queueSource,
      current_queue_size: currentQueueSize,
    },
  });
}
